import { useRouterState } from "@tanstack/react-router";
import clsx from "clsx";
import { Sparkles } from "lucide-react";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../api/client.js";
import { problemText } from "../ui/feedback.js";
import { useToast } from "../ui/toast.js";
import { resourceName, useAssistantStatus } from "./api.js";
import { chipOf, focusedTab, pageOf } from "./page.js";
import { assistant, useAssistant, waitingCount } from "./store.js";

/*
 * The chat button (docs/assistant.md, "The chat button and window"): in the
 * top bar next to the notifications, on every signed-in page (owner's
 * decision 2026-10-20: the floating button covered pages). Hidden without a model
 * provider, and for members while the organisation has it off. The window
 * loads with its first opening (assistant-ui comes with it).
 */

const AssistantWindow = lazy(() => import("./window.js"));

export function AssistantLauncher() {
  const { t } = useTranslation();
  const toast = useToast();
  const status = useAssistantStatus();
  const state = useAssistant();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const info = status.data;
  if (!info?.available || (!info.enabled && !info.isAdmin)) return null;

  const open = async () => {
    if (!info.enabled) return assistant.setView("docked");
    // In the session workspace (and from the console's address) the focused tab is the page.
    const page = pageOf(path, focusedTab(false));
    const label =
      page.resource && page.projectId
        ? await resourceName(
            `/projects/${page.projectId}/${page.resource.collection}/${page.resource.resourceId}`,
          )
        : null;
    try {
      await assistant.openFor(page, {
        projectId: page.projectId,
        title: label ? t("assistant.newAbout", { name: label }) : t("assistant.newConversation"),
        chip: label ? chipOf(page, label) : null,
      });
    } catch (error) {
      toast({
        tone: "danger",
        title:
          error instanceof ApiProblem && error.problem
            ? problemText(t, error.problem)
            : t("errors.codes.internal"),
      });
    }
  };
  const unread = state.unread.length;
  // Cards waiting for the person: the loaded list knows best, else the status.
  const waiting = state.conversations.length > 0 ? waitingCount(state.conversations) : info.waiting;
  const label =
    waiting > 0
      ? t("assistant.openWaiting", { n: waiting })
      : unread > 0
        ? t("assistant.openUnread", { n: unread })
        : t("assistant.open");
  return (
    <>
      <button
        type="button"
        onClick={() => (state.open ? assistant.close() : void open())}
        aria-label={label}
        aria-expanded={state.open}
        title={label}
        className={clsx(
          "relative flex items-center justify-center rounded-lg p-2 hover:bg-surface-2 hover:text-text",
          state.open ? "bg-accent-soft text-accent" : "text-muted",
        )}
      >
        <Sparkles className="size-5" aria-hidden />
        {waiting > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex min-w-5 items-center justify-center rounded-full bg-warn px-1 text-[0.7rem] font-semibold text-white"
            data-badge="waiting"
          >
            {waiting}
          </span>
        ) : (
          unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex min-w-5 items-center justify-center rounded-full bg-danger px-1 text-[0.7rem] font-semibold text-white">
              {unread}
            </span>
          )
        )}
      </button>
      {state.open && (
        <Suspense fallback={null}>
          <AssistantWindow status={info} />
        </Suspense>
      )}
    </>
  );
}
